import { Stock, WarehouseLocation } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import type { operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';

export type FetchItemStocksResponse = operations['get-inventory-item-stocks']['responses']['200']['content']['application/json'];
export type StockRowRaw = FetchItemStocksResponse[number];
export type StockLocationRaw = StockRowRaw['location'];

// Other modules import these names for stocks nested in their own responses,
// which carry the same stock row and location.
export type StockResponse = StockRowRaw;
export type WarehouseLocationResponse = StockLocationRaw;

// The traceability-group operations serialise the location without these three.
export function toWarehouseLocation(inv: Omit<StockLocationRaw, 'code' | 'created_at' | 'updated_at'>):WarehouseLocation<undefined, undefined, undefined> {
    return {
        id: inv.id,
        uuid: inv.uuid,
        warehouseId: inv.warehouse_id,
        warehouseUuid: inv.warehouse_uuid,
        warehouse: undefined,
        name: inv.name,
        storageType: inv.storage_type,
        storageAddress: inv.storage_address,
        deletedAt: inv.deleted_at,
        moveLines: undefined,
        stocks: undefined,
    };
}

export function toStock(inv: StockRowRaw): Stock<WarehouseLocation, undefined> {
    return {
        quantity: inv.quantity,
        quantityInStock: inv.quantity_in_stock,
        quantityExpected: inv.quantity_expected,
        quantityReserved: inv.quantity_reserved,
        quantityFree: inv.quantity_free,
        locationUuid: inv.location_uuid,
        location: toWarehouseLocation(inv.location),
        productUuid: inv.product_uuid,
        productTraceabilityGroupUuid: inv.product_traceability_group_uuid,
        item: undefined,
    };
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): { fetchItemStocks: (itemId: number) => Promise<Array<Stock<WarehouseLocation, undefined>>>; } => ({
    fetchItemStocks: async (itemId: number): Promise<Array<Stock<WarehouseLocation, undefined>>> => axios
        .get<FetchItemStocksResponse>(`/api/inventory/${itemId}/stocks`)
        .then(response => response.data.map(toStock)),
});
